/**
 * Pickup date/time rules for the client booking flow — shared so the Date/Time panel and the
 * (future) final booking submission validate identically and can't drift apart.
 *
 * A pickup is an absolute timestamp, so a time that was valid when picked can go stale if the tab is
 * left open (it slips into the past, or under the 5-minute lead). `validatePickup` must therefore run
 * BOTH at the panel's Proceed AND again immediately before the booking is created (final submit),
 * each time against a fresh `now`. The backend guarder (AustralianGuarder / USGuarder) is the final
 * backstop; this keeps the client from ever firing a request the server will reject.
 */

import { startOfDay } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";

/** Pickup times snap to a 5-minute grid — matches the old web/mobile booking flows and the backend. */
export const STEP_MINUTES = 5;
/** Backend rejects pickups less than 5 minutes away (AustralianGuarder / USGuarder). */
export const MIN_LEAD_MS = 5 * 60 * 1000;
/**
 * There is deliberately NO default pickup slot.
 *
 * `defaultPickupSlot()` used to live here: the current local time snapped to the nearest 5-minute mark,
 * seeded into the Date/Time panel's calendar and time fields so Proceed was actionable the moment the
 * panel opened. It was removed because "now" is almost never the journey being booked, and a pre-filled
 * answer is accepted by not being noticed — the client confirms a time they never chose. The fields open
 * empty instead, and `DateTimePanel` refuses Proceed until a date and a time have actually been picked.
 *
 * The mobile rider app still defaults its picker to "now"; the two surfaces diverge on this on purpose.
 */

/**
 * Validate a fully-resolved pickup timestamp against the not-past + 5-min-lead rules the backend
 * enforces. Returns a user-facing error message, or `null` when the pickup is valid.
 */
export const validatePickup = (pickup: Date, now: number = Date.now()): string | null => {
  if (pickup.getTime() < now) return "Pickup time can't be in the past";
  if (pickup.getTime() < now + MIN_LEAD_MS) {
    return "Your booking is less than 5 minutes away. Please contact us to make this booking.";
  }
  return null;
};

/**
 * The PICKUP's timezone (DEV-1857). Times typed into the panel are wall-clock times at the pickup, and the
 * backend reads them in the pickup location's zone, so "now" has to be measured there too, not in the
 * booker's browser. A Sydney operator moving a Brisbane pickup to 1:45 PM at 1:13 PM Brisbane time was told
 * the time was in the past, because it was already 2:13 PM in Sydney.
 *
 * Mirrors the backend's resolution: `AuFilter::getFormattedStateData` (state → city rule), then
 * `JobHydrator::getTimeZone` (city rule → zone), and the US `STATE_TIMEZONE` table in
 * `Location\Filter\CountryFilter\Us`. Any other country resolves like the backend's `ZZ/International`, to
 * Sydney.
 */
const AU_STATE_TIMEZONE: Record<string, string> = {
  NSW: "Australia/Sydney",
  ACT: "Australia/Sydney",
  VIC: "Australia/Melbourne",
  QLD: "Australia/Brisbane",
  SA: "Australia/Adelaide",
  WA: "Australia/Perth",
  TAS: "Australia/Hobart",
  NT: "Australia/Darwin",
};

/** Predominant zone per US state, copied from the backend's `Us::STATE_TIMEZONE`. */
const US_STATE_TIMEZONE: Record<string, string> = {
  AL: "America/Chicago",
  AK: "America/Anchorage",
  AZ: "America/Phoenix",
  AR: "America/Chicago",
  CA: "America/Los_Angeles",
  CO: "America/Denver",
  CT: "America/New_York",
  DE: "America/New_York",
  DC: "America/New_York",
  FL: "America/New_York",
  GA: "America/New_York",
  HI: "Pacific/Honolulu",
  ID: "America/Denver",
  IL: "America/Chicago",
  IN: "America/New_York",
  IA: "America/Chicago",
  KS: "America/Chicago",
  KY: "America/New_York",
  LA: "America/Chicago",
  ME: "America/New_York",
  MD: "America/New_York",
  MA: "America/New_York",
  MI: "America/New_York",
  MN: "America/Chicago",
  MS: "America/Chicago",
  MO: "America/Chicago",
  MT: "America/Denver",
  NE: "America/Chicago",
  NV: "America/Los_Angeles",
  NH: "America/New_York",
  NJ: "America/New_York",
  NM: "America/Denver",
  NY: "America/New_York",
  NC: "America/New_York",
  ND: "America/Chicago",
  OH: "America/New_York",
  OK: "America/Chicago",
  OR: "America/Los_Angeles",
  PA: "America/New_York",
  RI: "America/New_York",
  SC: "America/New_York",
  SD: "America/Chicago",
  TN: "America/Chicago",
  TX: "America/Chicago",
  UT: "America/Denver",
  VT: "America/New_York",
  VA: "America/New_York",
  WA: "America/Los_Angeles",
  WV: "America/New_York",
  WI: "America/Chicago",
  WY: "America/Denver",
};

const COUNTRY_TIMEZONE: Record<string, string> = {
  NZ: "Pacific/Auckland",
  GB: "Europe/London",
  CH: "Europe/Zurich",
  PH: "Asia/Manila",
};

/** Same fallback as the backend's `ZZ/International` and its default for an unrecognised AU state. */
const FALLBACK_TIMEZONE = "Australia/Sydney";

/**
 * IANA zone of the pickup, or `null` when there is no pickup (or it carries no country) yet. Callers treat
 * `null` as the browser's own zone, which is what the panel did before DEV-1857.
 */
export const pickupTimeZone = (pickup: Pick<PointsOfInterest, "country_code" | "state_code"> | null | undefined): string | null => {
  const country = pickup?.country_code?.trim().toUpperCase();
  if (!country) return null;
  const state = pickup?.state_code?.trim().toUpperCase() ?? "";
  if (country === "AU") return AU_STATE_TIMEZONE[state] ?? FALLBACK_TIMEZONE;
  if (country === "US") return US_STATE_TIMEZONE[state] ?? FALLBACK_TIMEZONE;
  return COUNTRY_TIMEZONE[country] ?? FALLBACK_TIMEZONE;
};

/**
 * The real instant of a wall-clock pickup. The panel keeps the typed time as a browser-local `Date` (the
 * "naive wall clock" the payload sends, see `flightPickup.ts`), so its `getTime()` is that wall clock in the
 * BROWSER's zone. Re-reading its fields in the pickup's zone gives the instant `validatePickup` must compare
 * against `now`. The committed value is unchanged.
 */
export const wallClockInstant = (wall: Date, timeZone: string | null): Date => (timeZone ? zonedTimeToUtc(wall, timeZone) : wall);

/** Midnight of TODAY at the pickup, as a browser-local `Date`, which is how the calendar reads its `minDate`. */
export const todayAtPickup = (timeZone: string | null, now: Date = new Date()): Date =>
  startOfDay(timeZone ? utcToZonedTime(now, timeZone) : now);
